import type { ContentType } from "@brookbush/database";

import { parseAsBoolean, useQueryState } from "nuqs";
import { create } from "zustand";

import { createSelectors } from "~/utils/helpers";

export interface ChatPageContent {
	contentType: ContentType;
	contentId: string;
	title: string;
}

interface GlobalStore {
	isNotificationsPanelOpen: boolean;
	isSearchModalOpen: boolean;
	toggleNotificationsPanel: () => void;
	toggleSearchModal: () => void;
	promoCodeParam: string | null;
	isGoogleSignInSupported: boolean;
	hasActiveTutorial: boolean;

	hasTimeOnSite?: boolean;
	hasScrolled?: boolean;

	setHasTimeOnSite: (val: boolean) => void;
	setHasScrolled: (val: boolean) => void;

	setHasActiveTutorial: (val: boolean) => void;

	setPromoCodeParam: (val: string | null) => void;
	setIsGoogleSignInSupported: (val: boolean) => void;

	activeChatThreadId: number | null;
	isChatBarOpen: boolean;
	isChatImmersive: boolean;
	chatPageContent: ChatPageContent | null;
	setChatPageContent: (content: ChatPageContent | null) => void;
	setActiveChatThreadId: (id: number | null) => void;
	/** First message for a new tutor thread, sent once a content page mounts. */
	pendingChatPrompt: string | null;
	setPendingChatPrompt: (prompt: string | null) => void;
	openChatBar: () => void;
	closeChatBar: () => void;
	expandChat: () => void;
	collapseChat: () => void;
}

const useGlobalStoreBase = create<GlobalStore>((set) => ({
	isSearchModalOpen: false,
	promoCodeParam: null,
	isNotificationsPanelOpen: false,
	isGoogleSignInSupported: true,
	hasActiveTutorial: false,
	hasTimeOnSite: false,
	hasScrolled: false,

	setHasTimeOnSite: (hasTimeOnSite: boolean) => set({ hasTimeOnSite }),
	setHasScrolled: (hasScrolled: boolean) => set({ hasScrolled }),

	setHasActiveTutorial: (hasActiveTutorial: boolean) =>
		set({ hasActiveTutorial }),

	setIsGoogleSignInSupported: (isGoogleSignInSupported) =>
		set({
			isGoogleSignInSupported,
		}),

	toggleNotificationsPanel: () =>
		set(({ isNotificationsPanelOpen }) => ({
			isNotificationsPanelOpen: !isNotificationsPanelOpen,
		})),

	toggleSearchModal: () =>
		set(({ isSearchModalOpen }) => ({
			isSearchModalOpen: !isSearchModalOpen,
		})),

	setPromoCodeParam: (promoCodeParam) =>
		set({
			promoCodeParam,
		}),

	activeChatThreadId: null,
	isChatBarOpen: false,
	isChatImmersive: false,
	chatPageContent: null,
	setChatPageContent: (chatPageContent) => set({ chatPageContent }),
	setActiveChatThreadId: (activeChatThreadId) => set({ activeChatThreadId }),
	pendingChatPrompt: null,
	setPendingChatPrompt: (pendingChatPrompt) => set({ pendingChatPrompt }),
	openChatBar: () => set({ isChatBarOpen: true }),
	closeChatBar: () => set({ isChatBarOpen: false, isChatImmersive: false }),
	expandChat: () => set({ isChatImmersive: true }),
	collapseChat: () => set({ isChatImmersive: false }),
}));

const useGlobalStoreWithSelectors = createSelectors(useGlobalStoreBase);

const useGlobalStore = () => {
	const store = useGlobalStoreWithSelectors();

	const [isAnnotationsPanelOpen, setIsAnnotationsPanelOpen] = useQueryState(
		"annotations-panel",
		parseAsBoolean.withOptions({ history: "push" }),
	);

	const toggleAnnotationsPanel = () => {
		setIsAnnotationsPanelOpen(!isAnnotationsPanelOpen ? true : null);
	};

	return {
		...store,
		isAnnotationsPanelOpen,
		toggleAnnotationsPanel,
	};
};

export default useGlobalStore;
